import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isBefore, isAfter } from 'date-fns';
import { PublicLayout } from '../../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../../components/shared/SEOMetaTags';
import { getEventById, getEventBySlug } from '../../../services/event.service';
import { Event } from '../../../types/event';
import EventDetails from '../../../components/event/EventDetails';
import { MapPinIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;

      try {
        setLoading(true);
        // Essayer de récupérer l'événement par ID ou slug
        let eventData;
        
        // Si l'ID est un UUID, on suppose que c'est un ID
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
        
        if (isUuid) {
          eventData = await getEventById(id);
          // Si l'événement a un slug, rediriger vers l'URL avec le slug
          if (eventData?.slug) {
            navigate(`/events/${eventData.slug}`, { replace: true });
            return;
          }
        } else {
          // Sinon, essayer de récupérer par slug
          eventData = await getEventBySlug(id);
        }
        
        setEvent(eventData);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'événement:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, navigate]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!event) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-red-500 mb-4">Événement non trouvé</h1>
            <p className="text-muted-foreground mb-6">L'événement que vous recherchez n'existe pas ou a été supprimé.</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const eventDate = new Date(event.start_date);
  const isUpcoming = isAfter(eventDate, new Date());
  const isPast = isBefore(new Date(event.end_date || event.start_date), new Date());

  return (
    <PublicLayout>
      <SEOMetaTags 
        title={`${event.title} | VotAfrica`}
        description={event.description?.substring(0, 160) || 'Découvrez cet événement exceptionnel sur VotAfrica'}
        image={event.image_url || undefined}
      />
      
      <div className="min-h-screen relative pt-[8.5rem] md:pt-[3.5rem]">
        {/* Background principal avec l'image de l'événement */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: event.image_url ? `url(${event.image_url})` : 'url(https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1500&q=80)',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backgroundAttachment: 'fixed',
            filter: 'brightness(0.5) blur(2px)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-black/95" />
        </div>

        {/* HEADER LUXUEUX */}
        <div className="relative z-10 w-full">
          <div className="w-full max-w-none">
            <div className="relative w-full h-[240px] sm:h-[340px] md:h-[400px] flex items-end justify-center">
              {/* Overlay glassmorphism */}
              <div className="relative z-10 w-full max-w-4xl mx-auto rounded-t-2xl sm:rounded-2xl shadow-2xl p-4 sm:p-8 md:p-12 bg-black/30 backdrop-blur-md border border-yellow-400/20" style={{boxShadow:'0 8px 32px 0 rgba(255,215,0,0.28), 0 1.5px 0 0 #FFD700'}}>
                <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6">
                  <div className="flex-shrink-0">
                    {event.image_url && (
                      <div className="w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden border-4 border-yellow-400/50 shadow-lg bg-black/40">
                        <img
                          src={event.image_url}
                          alt={event.title}
                          className="w-full h-full object-cover object-center"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-center sm:text-left">
                    {/* Badge de catégorie avant le titre uniquement sur mobile */}
                    <div className="sm:hidden flex justify-center mb-2">
                      <span className="inline-block px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 font-semibold text-xs shadow-sm border border-yellow-400/30">
                        {event.category || 'Événement'}
                      </span>
                    </div>
                    
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white drop-shadow-lg mb-3" style={{textShadow:'0 2px 8px #000,0 0 0 #FFD700', fontFamily: 'var(--font-primary), sans-serif'}}>
                      {event.title}
                    </h1>
                    
                    {/* Bloc original des badges (sans catégorie sur mobile) */}
                    <div className="flex flex-wrap justify-center sm:justify-start items-center gap-2 sm:gap-3 mb-2">
                      <span className="hidden sm:inline-block px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 font-semibold text-xs md:text-sm shadow-sm border border-yellow-400/30">
                        {event.category || 'Événement'}
                      </span>
                      <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-white font-medium text-xs md:text-sm flex items-center">
                        <MapPinIcon className="w-3 h-3 mr-1.5" />
                        {event.location || 'Lieu à venir'}
                      </span>
                      <span className="inline-block px-3 py-1 rounded-full bg-yellow-400/20 text-yellow-100 font-medium text-xs md:text-sm flex items-center">
                        <CalendarDaysIcon className="w-3 h-3 mr-1.5" />
                        {event.start_date ? new Date(event.start_date).toLocaleDateString('fr-FR', {weekday:'long', year:'numeric', month:'long', day:'numeric'}).replace(/^\w/, c => c.toUpperCase()) : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12">
          <EventDetails event={event} />
        </div>
      </div>
    </PublicLayout>
  );
};

export default EventDetailPage;
